import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "chameleon/lib/components";

import {
  CurrentShippingAddress,
  FormCurrentAddress,
} from "checkout-experience/components/forms/form-current-address";
import { wrapper } from "checkout-experience/components/forms/form-current-address.css";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { countryISOMapping } from "checkout-experience/utils/mappers/countryIso3to2";
import { usePaymentForm } from "checkout-query-client/hooks/use-form";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";

import { usePaymentOptionContext } from "../payment-method/payment-option-provider";

interface Props {
  currentShippingAddress?: CurrentShippingAddress;
  value?: string;
}

export const FormCurrentAddressWithEdit = ({
  currentShippingAddress,
}: Props) => {
  const { t } = useTranslation("combined");
  if (!currentShippingAddress) return <></>;

  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { formId = "", paymentType } = paymentOption ?? {};
  const { checkoutSession, merchantSettings, checkoutSessionToken } =
    useChexContext();
  const { data: formConfig } = usePaymentForm({
    checkoutSession,
    merchantSettings,
    paymentType,
    formId,
    checkoutSessionToken,
  });
  const { watch, setValue } = useFormContext();
  const {
    billingAddressCity,
    billingAddressAddressLine1,
    billingAddressAddressLine2,
    billingAddressCountry,
    billingAddressAdministrativeArea,
    billingAddressZip,
  } = (watch(formId) ?? {}) as ChexFieldValues;
  const { shippingAddress, billingAddress, billingAddressCollection } =
    currentShippingAddress || {};

  const billingAddressValue = useMemo(() => {
    const addressLines = [];
    if (billingAddressCity) {
      addressLines.push(billingAddressAddressLine1 ?? "");
      addressLines.push(billingAddressAddressLine2 ?? "");
    } else if (billingAddress?.addressLines?.length) {
      addressLines.push(billingAddress?.addressLines?.[0] ?? "");
      addressLines.push(billingAddress?.addressLines?.[1] ?? "");
    } else if (shippingAddress?.line1) {
      addressLines.push(shippingAddress?.line1 ?? "");
      addressLines.push(shippingAddress?.line2 ?? "");
    }

    return {
      regionCode:
        billingAddressCountry ||
        (billingAddress?.regionCode ?? shippingAddress?.regionCode),
      addressLines: addressLines,
      locality:
        billingAddressCity ||
        (billingAddress?.locality ?? shippingAddress?.locality),
      administrativeArea:
        billingAddressAdministrativeArea ||
        (billingAddress?.administrativeArea ??
          shippingAddress?.administrativeArea),
      postalCode:
        billingAddressZip ||
        (billingAddress?.postalCode ?? shippingAddress?.postalCode),
    };
  }, [shippingAddress, billingAddress, watch(formId)]);

  const onClickEditAddress = () => {
    setValue(`${formId}.currentShippingAddressWithEdit`, "true");
    const billingFields = Object.keys(formConfig?.fields ?? {}).filter(
      (fieldId) => fieldId.includes("billingAddress")
    );

    let countryValue = billingAddressValue?.regionCode ?? "";
    countryValue =
      countryValue.length > 2 ? countryISOMapping[countryValue] : countryValue;

    if (
      billingFields.includes("billingAddressCountry") &&
      countryValue !== "US"
    ) {
      setValue(`${formId}.billingAddressCountry`, countryValue);
    }
    if (billingFields.includes("billingAddressAddressLine1")) {
      setValue(
        `${formId}.billingAddressAddressLine1`,
        billingAddressCollection?.collectAddressCityState
          ? (billingAddressValue?.addressLines?.[0] ?? "")
          : ""
      );
    }
    if (billingFields.includes("billingAddressAddressLine2")) {
      setValue(
        `${formId}.billingAddressAddressLine2`,
        billingAddressCollection?.collectAddressLine2
          ? (billingAddressValue?.addressLines?.[1] ?? "")
          : ""
      );
    }
    if (
      billingFields.includes("billingAddressCity") &&
      billingAddressCollection?.collectAddressCityState
    ) {
      setValue(`${formId}.billingAddressCity`, billingAddressValue?.locality);
    }
    if (billingFields.includes("billingAddressAdministrativeArea")) {
      setValue(
        `${formId}.billingAddressAdministrativeArea`,
        billingAddressCollection?.collectAddressCityState
          ? billingAddressValue?.administrativeArea
          : ""
      );
    }
    if (billingFields.includes("billingAddressZip")) {
      setValue(`${formId}.billingAddressZip`, billingAddressValue?.postalCode);
    }
  };

  // hide entire address if we don't have billing address collection on
  if (!currentShippingAddress.billingAddressCollection?.enabled) {
    return null;
  }

  return (
    <div className={wrapper}>
      <FormCurrentAddress
        currentShippingAddress={{
          ...(currentShippingAddress || {}),
          billingAddress: billingAddressValue,
        }}
        withEdit
      />
      <Button
        id="edit-address-btn"
        testId="editAddressBtn"
        variant="text"
        text={t("edit")}
        onClick={onClickEditAddress}
      />
    </div>
  );
};

FormCurrentAddressWithEdit.displayName = "FormCurrentAddress";
